<template>
  <div class="w-full flex flex-col gap-4">
    <!-- Search Input -->
    <div class="relative">
      <input
        v-model="searchQuery"
        type="text"
        :placeholder="$t('currencySelector.searchPlaceholder')"
        class="w-full px-4 py-2.5 pl-10 bg-white/10 border border-white/20 rounded-lg text-sm text-vault-text-primary placeholder-white/40 focus:outline-none focus:border-white/40 transition-all duration-200"
      />
      <Search :size="16" class="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center py-8">
      <div class="spinner"></div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="p-4 bg-red-500/5 border border-red-500/20 rounded-lg text-center">
      <p class="text-xs text-red-500/80 mb-3">{{ error }}</p>
      <button
        type="button"
        class="btn-primary !px-4 !py-2 !text-xs"
        @click="loadOptions"
      >
        {{ $t('common.retry') }}
      </button>
    </div>

    <!-- Options List -->
    <div v-else class="flex flex-col gap-2 max-h-[400px] overflow-y-auto custom-scrollbar">
      <!-- Popular Section -->
      <div v-if="!searchQuery && popularOptions.length > 0" class="flex flex-col gap-2 mb-2">
        <div class="text-xs font-semibold text-vault-text-secondary uppercase tracking-wide px-2">
          {{ $t('currencySelector.popular') }}
        </div>
        <DepositOption
          v-for="option in popularOptions"
          :key="getOptionKey(option)"
          :option="option"
          @select="selectOption(option)"
        />
      </div>

      <!-- All Options -->
      <div v-if="!searchQuery" class="flex flex-col gap-2">
        <div class="text-xs font-semibold text-vault-text-secondary uppercase tracking-wide px-2">
          {{ $t('currencySelector.allNetworks') }}
        </div>
        <DepositOption
          v-for="option in nonPopularOptions"
          :key="getOptionKey(option)"
          :option="option"
          @select="selectOption(option)"
        />
      </div>

      <!-- Search Results -->
      <div v-else class="flex flex-col gap-2">
        <DepositOption
          v-for="option in filteredOptions"
          :key="getOptionKey(option)"
          :option="option"
          @select="selectOption(option)"
        />
        <div v-if="filteredOptions.length === 0" class="text-center py-8 text-vault-text-secondary text-sm">
          {{ $t('currencySelector.noResults') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { Search } from 'lucide-vue-next'
import { useCryptoDeposit } from '../../composables/useCryptoDeposit'
import DepositOption from './DepositOption.vue'

const { t: $t } = useI18n()

const props = defineProps({
  walletAddress: {
    type: String,
    required: true
  },
  walletCurrency: {
    type: Object, // CurrencyConfig
    required: true
  },
  walletNetwork: {
    type: Object, // { id, name }
    required: true
  }
})

const emit = defineEmits(['select'])

// Composable
const cryptoDeposit = useCryptoDeposit({
  walletAddress: props.walletAddress,
  walletCurrency: props.walletCurrency,
  walletNetwork: props.walletNetwork,
  loadRatesOnInit: false // Don't load rates - they're not needed for currency selection
})

// State
const searchQuery = ref('')

// Computed
const loading = computed(() => cryptoDeposit.loading.value)
const error = computed(() => cryptoDeposit.error.value?.message || null)

const popularOptions = computed(() => cryptoDeposit.popularOptions.value)

const nonPopularOptions = computed(() =>
  cryptoDeposit.depositOptions.value.filter(opt => !opt.isPopular)
)

const filteredOptions = computed(() => {
  if (!searchQuery.value) {
    return cryptoDeposit.depositOptions.value
  }

  const query = searchQuery.value.toLowerCase()
  return cryptoDeposit.depositOptions.value.filter(option => {
    const currency = option.currency
    const network = option.network

    return (
      currency.ticker.toLowerCase().includes(query) ||
      currency.name?.toLowerCase().includes(query) ||
      currency.fullName?.toLowerCase().includes(query) ||
      network?.name.toLowerCase().includes(query) ||
      network?.blockchain.toLowerCase().includes(query)
    )
  })
})

// Methods
function getOptionKey(option) {
  return `${option.currency.ticker}_${option.network?.id || 'default'}`
}

async function loadOptions() {
  await cryptoDeposit.loadDepositOptions()
}

function selectOption(option) {
  emit('select', option)
}

// Lifecycle
onMounted(() => {
  loadOptions()
})
</script>

<style scoped>
/* Loading Spinner */
.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-top-color: rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Custom Scrollbar */
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 3px;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 3px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.3);
}
</style>
